import { v1AuthStartOidcLogin } from '@repo/sdk/gen'
import { Spinner } from '@repo/ui/spinner'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { useEffect } from 'react'
import { z } from 'zod'
import i18n, { isAppLanguageCode, useTranslation } from '@/lib/i18n'
import { BrandCanvas } from '@/routes/-components/brand-canvas'
import {
	forgetOrganization,
	rememberOrganization
} from '@/routes/-lib/last-organization'

const searchSchema = z.object({
	returnPath: z.string().optional()
})

/**
 * Starts the OIDC round trip for one organization (`auth/frontend.md`'s
 * "Sign in" flow, step 1) and hands the browser off to Keycloak's own hosted
 * login page — nothing renders here beyond a loading state. `ssr: false`:
 * the redirect is `window.location`, a browser-only API, and there is
 * nothing here worth a server-rendered first response.
 *
 * An unrecognized or currently-unavailable slug comes back as one refusal
 * whichever it was — `auth/frontend.md`'s anti-enumeration guarantee — and
 * that refusal is a typo, not a broken application. It used to reach the
 * app's default error boundary, which met a mistyped name with «Something
 * went wrong» and two buttons that both led away from the one field that
 * could fix it. The name goes back to the form it was typed in, which says
 * so under the input and keeps what was typed.
 */
export const Route = createFileRoute('/(signed-out)/o/$slug/login/')({
	ssr: false,
	validateSearch: searchSchema,
	loaderDeps: ({ search }) => ({ returnPath: search.returnPath }),
	loader: async ({ params, deps }) => {
		// `throwOnError: false` and not a `catch`: the refusal is the ordinary
		// answer here, and the SDK resolves to `undefined` for it.
		const response = await v1AuthStartOidcLogin({
			body: {
				slug: params.slug,
				returnPath: deps.returnPath,
				// Keycloak's page opens in the language this one is showing,
				// rather than in the instance default.
				locale: currentLocale()
			},
			throwOnError: false
		})

		if (!response) {
			// A remembered name that stopped resolving (the organization was
			// deactivated or removed) would otherwise send `/login` straight
			// back here on every visit.
			forgetOrganization(params.slug)
			throw redirect({
				search: {
					slug: params.slug,
					unknownOrganization: true,
					...(deps.returnPath ? { returnPath: deps.returnPath } : {})
				},
				to: '/login'
			})
		}

		rememberOrganization(params.slug)
		return response.data
	},
	component: RedirectToKeycloak
})

function currentLocale() {
	const language = i18n.resolvedLanguage
	return isAppLanguageCode(language) ? language : undefined
}

function RedirectToKeycloak() {
	const { authorizationUrl } = Route.useLoaderData()
	const { t } = useTranslation()

	// `replace`: this spinner is a step, not a page, and an entry for it in
	// the history is what back would return to after the sign-in.
	useEffect(() => {
		window.location.replace(authorizationUrl)
	}, [authorizationUrl])

	return (
		<BrandCanvas className='grid min-h-full w-full place-items-center p-4'>
			<Spinner
				aria-label={t('client:login.redirecting')}
				className='size-6 text-primary'
			/>
		</BrandCanvas>
	)
}
